/**
 * Copyright(c) 2024 Magrabi.
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information of Magrabi ("Confidential
 * Information"). You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the contract agreement you entered into with Magrabi.
 *
 * @module Algolia
 *
 * @author Naseef O
 *
 */

import { liteClient as algoliasearch } from "algoliasearch/lite";

import {
  AttributeTypes,
  editsSectionType,
  recommendationType,
  salePageConstants,
} from "../../apiUtils/constants";
import {
  constructIndexName,
  decimalSeparatedPrice,
  getCurrencyByLocale,
  getErrorMessage,
  getRecommendationModels,
  getValueBasedOnLanguage,
  hasArrayElements,
} from "../../apiUtils/commonUtils";
import {
  algoliaEventParam,
  algoliaRecommendationParam,
  algoliaResults,
  hitsPerPage,
} from "../../apiUtils/algoliaUtils/constants";
import {
  AlgoliaProductItem,
  AlgoliaResponse,
  AlgoliaSearchParams,
  AlgoliaSearchResult,
  AlgoliaSearchSuggestionResult,
  CurrentRefinements,
  FacetObject,
  FilteredFacet,
  MultiQueryItem,
  MultipleQueries,
  ProductItem,
  RecommendProductItem,
  RecommendedRequest,
  SalePageAlgoliaResponse,
  SalePageMultiQuery,
  SearchResponse,
  SortItem,
} from "../../apiUtils/algoliaUtils/types";

import {
  EditsItemAttribute,
  EditsType,
  FacetConfigurationsMappingType,
  FacetsData,
  ProductType,
  RangeFacetData,
  Recommendation,
  RecommendationType,
  TranslationData,
} from "../../common/lib/types";
import algoliaInsights from "search-insights";
// import algoliaInsights from "@components/AlgoliaInsight";
import { InsightsMethodMap } from "search-insights";
import {
  recommendClient as createRecommendClient,
  RecommendModels,
  RecommendationsRequest,
} from "@algolia/recommend";

import { getCategoryBySlug } from "../../api/saleor/services";
import { SearchForFacetValuesResponse } from "instantsearch.js";
import { LanguageCodeEnum } from "../../api/saleor/generated/graphql";
import { getSortedVariantImages, sortProductVariants } from "../../apiUtils/productUtils";
import { algoliaConfig } from "@/runtimeConfig/public";
import { getLangFromLocale, getCountryCodeFromLocale } from "@/utils/common";
import { algoliaRecommendationsCached, algoliaSearchCached } from "./actions";
import {
  assembleCarousel,
  buildRecommendRequests,
  getManualSourceValues,
  groupRecommendationsByModel,
  isAnchorProduct,
  isSkuSource,
} from "./recommendationUtils";

import { currencyMappings } from "@/runtimeConfig/server";

const { indexName: indexNamePrefix, applicationID, searchApiKey } = algoliaConfig || {};

/** Creating Algolia search client */
export const searchClient = algoliasearch(applicationID, searchApiKey);
/** Creating Algolia recommend client */
const recommendClient = createRecommendClient(applicationID, searchApiKey);
/**
 * Retrieves the parameter key using the provided attribute value.
 * @param {string} value - The attribute value.
 * @returns {string|undefined} The parameter key associated with the provided value.
 */
export const getAttributeQueryParamKey = (
  value: string,
  language: string,
  filterMappings: Record<string, string>,
) => {
  return Object.keys(filterMappings).find((key) => filterMappings[key] === value);
};

/**
 * @description Retrieves the selected filters based on the search parameters.
 * @param {AlgoliaSearchParams} searchParams - The Algolia search parameters.
 * @returns {Array<Array<string>>} An array containing arrays of selected filters.
 */

/**
 * @description Retrieves the selected filters from a provided mapping.
 * @param {AlgoliaSearchParams} searchParams - The Algolia search parameters.
 * @param {Record<string, string>} filterMappings - The filter mappings to use.
 * @returns {Array<Array<string>>} An array containing arrays of selected filters.
 */
const getSelectedFiltersFromMap = (
  searchParams: AlgoliaSearchParams,
  filterMappings: Record<string, string>,
): Array<Array<string>> => {
  return Object.entries(filterMappings).filter(([key]) => !!searchParams?.[key]);
};

/**
 * @description Retrieves the current refinements based on the selected filters and search parameters.
 * @param {Array<Array<string>>} selectedFilters - An array containing arrays of selected filters.
 * @param {AlgoliaSearchParams} searchParams - The Algolia search parameters.
 * @returns {Array<{ [key: string]: Array<string> }>} An array containing objects representing current refinements.
 */
const constructCurrentRefinements = (
  selectedFilters: Array<Array<string>>,
  searchParams: AlgoliaSearchParams,
): Array<{ [key: string]: Array<string> }> => {
  return selectedFilters.map(([key, attributeName]) => {
    const items = searchParams[key]?.split("|").filter(Boolean) ?? [];
    return { [attributeName]: items };
  });
};

/**
 * @description Retrieves facet filters based on the selected filters and search parameters.
 * @param {string[][]} selectedFilters - An array containing arrays of selected filters.
 * @param {AlgoliaSearchParams} searchParams - The Algolia search parameters.
 * @returns {Array<{ [key: string]: Array<string> }>} An array containing objects representing facet filters.
 */
const constructFacetFilters = (
  selectedFilters: string[][],
  searchParams: AlgoliaSearchParams,
): Array<{ [key: string]: Array<string> }> => {
  return selectedFilters.map(([key, attributeName]) => {
    const brands = searchParams[key]?.split("|").filter(Boolean) ?? [];
    const brandQ = brands.map((item) => `${attributeName}:${item}`);
    return { [attributeName]: brandQ };
  });
};

/**
 * @description Retrieves price filters based on the search parameters and current refinements.
 * @param {AlgoliaSearchParams} searchParams - The Algolia search parameters.
 * @param {Array<{ [key: string]: Array<string> }>} currentRefinements - An array containing objects representing current refinements.
 * @returns {Array<string>} An array containing price filters.
 */
const constructPriceFilters = (
  searchParams: AlgoliaSearchParams,
  currentRefinements: Array<{ [key: string]: Array<string> }>,
  currency: string,
): Array<string> => {
  const priceFacet = facetAttributeNames?.price;
  let priceFilters: Array<string> = [];
  if (searchParams?.pmin && searchParams?.pmax) {
    const pmin = `${priceFacet}>=${searchParams?.pmin}`;
    const pmax = `${priceFacet}<=${searchParams?.pmax}`;
    priceFilters = [pmin, pmax];
    currentRefinements.push({
      [priceFacet]: [
        `${decimalSeparatedPrice(parseFloat(searchParams?.pmin))} ${currency} - ${decimalSeparatedPrice(parseFloat(searchParams?.pmax))} ${currency}`,
      ],
    });
  }
  return priceFilters;
};

/**
 * @description Constructs a multi-query object for Algolia search.
 * @param {string} indexName - The name of the index.
 * @param {Array<{ [key: string]: Array<string> }>} facetFilters - An array containing objects representing facet filters.
 * @param {AlgoliaSearchParams} searchParams - The Algolia search parameters.
 * @param {Array<string>} priceFilters - An array containing price filters.
 * @param {string} categoryFilter - The category filter.
 * @param {boolean} pagination - Flag indicating whether pagination is enabled.
 * @returns {Array<MultiQueryItem>} An array containing multi-query items.
 */
const constructQuery = (
  indexName: string,
  facetFilters: Array<{ [key: string]: Array<string> }>,
  searchParams: AlgoliaSearchParams,
  priceFilters: Array<string>,
  categoryFilter: string,
  pagination: boolean,
  language: string,
  query?: string,
  filterMappings: Record<string, string> = {},
) => {
  const staticFacets = [...getTranslatedFacetsAttributes(language)];
  // Merge in any dynamic fields from filterMappings not already in the static list
  const dynamicFacets = Object.values(filterMappings).filter(
    (field) => !staticFacets.includes(field),
  );
  const facets = [...staticFacets, ...dynamicFacets];
  const priceFacet = facetAttributeNames?.price;
  const multiQuery: Array<MultiQueryItem> = [
    {
      main: {
        indexName,
        query,
        ...(pagination ? { page: parseInt(searchParams?.page ?? "1", 10) - 1 } : {}),
        facetFilters: facetFilters.map((obj) => Object.values(obj)[0]),
        hitsPerPage,
        numericFilters: priceFilters,
        facets: facets,
        filters: categoryFilter,
      },
    },
    {
      allFacets: {
        indexName,
        query,
        hitsPerPage: 0,
        facets: facets,
        filters: categoryFilter,
      },
    },
    {
      [priceFacet]: {
        indexName,
        query,
        facetFilters: facetFilters?.length
          ? facetFilters.map((obj) => Object.values(obj)[0])
          : [],
        hitsPerPage: 0,
        facets: priceFacet,
        filters: categoryFilter,
      },
    },
  ];

  /** Constructing multi query based on selected facets */
  Object.entries(filterMappings)
    .filter(([key]) => !!searchParams?.[key])
    .forEach(([, attributeName]) => {
      multiQuery.push({
        [attributeName]: {
          indexName,
          query,
          facetFilters: facetFilters
            ?.filter((obj) => !Object.keys(obj).includes(attributeName))
            .map((obj) => Object.values(obj)[0]),
          hitsPerPage: 0,
          numericFilters: priceFilters,
          facets: [attributeName],
          filters: categoryFilter,
        },
      });
    });

  return multiQuery;
};

/**
 * @description Retrieves current refinements based on the provided input items.
 * @param {Array<{ [key: string]: string[] }>} itemIn - An array of objects containing key-value pairs representing refinement items.
 * @returns {CurrentRefinements} An array containing current refinements.
 */
export const getRefinements = (
  itemIn: {
    [key: string]: string[];
  }[],
): CurrentRefinements => {
  const refinements: CurrentRefinements = [];
  itemIn?.forEach((item) => {
    item[Object.keys(item)[0]]?.forEach((value) => {
      const refinement = {
        label: value,
        value,
        attribute: Object.keys(item)[0],
      };
      refinements.push(refinement);
    });
  });
  return refinements;
};

export const transFormVariant = (
  variant: Record<string, any>,
  currentLanguage: string,
  defaultLanguage: string,
) =>
  variant
    ? {
        ...variant,
        variantName: variant?.variantName?.[currentLanguage],
        attributes: variant?.attributes?.[currentLanguage],
        availableBadges: transformBadges(variant?.availableBadges, currentLanguage),
        availablePromotion: transformPromotions(
          variant?.availablePromotion,
          currentLanguage,
          defaultLanguage,
        ),
        media: getSortedVariantImages(variant?.media),
        landingPageBaseUrl: variant?.url,
        groupIndex: variant?.groupIndex, // ← NEW: Include groupIndex for base/intermediate product filtering

        //For GA
        variantNameDefault: variant?.variantName?.[defaultLanguage],
        defaultAttributes: variant?.attributes?.[defaultLanguage],
      }
    : null;

export const transformVariants = (
  variants: Array<Record<string, any>>,
  currentLanguage: string,
  defaultLanguage: string,
) => {
  if (hasArrayElements(variants)) {
    return variants.map((variant) => transFormVariant(variant, currentLanguage, defaultLanguage));
  }
  return [];
};

export const transformAndSortVariants = (
  variants: Array<Record<string, any>>,
  currentLanguage: string,
  defaultLanguage: string,
  defaultVariantId: string,
) => {
  const transformedVariants = transformVariants(variants, currentLanguage, defaultLanguage);
  return sortProductVariants(transformedVariants as Array<Record<string, any>>, defaultVariantId);
};

export const transformPromotions = (
  availablePromotions: Array<Record<string, any>>,
  currentLanguage: string,
  defaultLanguage: string,
) => {
  if (hasArrayElements(availablePromotions)) {
    return availablePromotions.map((availablePromotion) => ({
      ...availablePromotion,
      promotion: {
        ...availablePromotion?.promotion,
        name: availablePromotion?.promotion?.name?.[currentLanguage],
        defaultName: availablePromotion?.promotion?.name?.[defaultLanguage],
      },
    }));
  }
  return [];
};

export const transformBadges = (
  availableBadges: Array<Record<string, any>>,
  currentLanguage: string,
) => {
  if (hasArrayElements(availableBadges)) {
    return availableBadges.map((availableBadge) => ({
      ...availableBadge,
      name: availableBadge?.name?.[currentLanguage],
    }));
  }
  return [];
};

export const transformHits = (
  hit: any,
  currentLanguage: string,
  defaultLanguage: string,
  queryID: string = "",
  indexName: string = "",
) =>
  hit
    ? {
        ...hit,
        name: hit?.name?.[currentLanguage] ?? hit?.name?.[defaultLanguage] ?? hit?.name,
        categories:
          hit?.categories?.[currentLanguage] ??
          hit?.categories?.[defaultLanguage] ??
          hit?.categories,
        defaultCategoryDetails: hit?.categories?.[defaultLanguage],
        brand: hit?.brand && {
          ...hit?.brand,
          name:
            hit?.brand?.name?.[currentLanguage] ??
            hit?.brand?.name?.[defaultLanguage] ??
            hit?.brand?.name,
        },
        attributes:
          hit?.attributes?.[currentLanguage] ??
          hit?.attributes?.[defaultLanguage] ??
          hit.attributes,
        defaultVariant: transFormVariant(hit?.defaultVariant, currentLanguage, defaultLanguage),
        variantColorMappings: transformAndSortVariants(
          hit?.variantColorMappings as Array<Record<string, any>>,
          currentLanguage,
          defaultLanguage,
          hit?.defaultVariant?.variantId,
        ),
        availableBadges: transformBadges(hit?.availableBadges, currentLanguage),
        media: getSortedVariantImages(hit?.media),

        //For GA
        defaultName: hit?.name?.[defaultLanguage],
        defaultCategoryName: hit?.categories?.[defaultLanguage]?.lvl0,
        defaultBrandName: hit?.brand?.name?.[defaultLanguage],

        //For algolia event
        queryID,
        indexName,
      }
    : {};

export const transformAlgoliaResponse = (
  results: Array<SearchResponse<ProductItem>>,
  language: string = "",
): Array<SearchResponse<ProductItem>> => {
  const defaultLanguage = "en";
  const currentLanguage: string = !language ? defaultLanguage : language;
  const translatedResults: SearchResponse<ProductItem>[] = [];
  if (hasArrayElements(results)) {
    results.forEach((result) => {
      if (hasArrayElements(result?.hits)) {
        result.hits = result.hits.map((hit) =>
          transformHits(hit, currentLanguage, defaultLanguage, result?.queryID, result?.index),
        );
        result.nbHits = result?.nbSortedHits ? result?.nbSortedHits : result?.nbHits;
      }
      translatedResults.push(result);
    });
  }
  return translatedResults;
};

/**
 * @description Performs refinement operations on Algolia search results.
 * @param {string} categoryFilter - The category filter.
 * @param {string} indexName - The name of the index.
 * @param {AlgoliaSearchParams} searchParams - The Algolia search parameters.
 * @param {string} language - The language for the search.
 * @param {string} locale - The locale for the search.
 * @param {boolean} [pagination=false] - Flag indicating whether pagination is enabled.
 * @param {string} query - Optional search query string.
 * @param {FilteredFacet[]} filteredFacets - Optional array of filter configurations for dynamic mapping.
 * @returns {Promise<Array<AlgoliaResponse>>} A promise resolving to an array containing refined Algolia search responses.
 */
export const algoliaRefine = async (
  categoryFilter: string,
  indexName: string,
  searchParams: AlgoliaSearchParams,
  language: string,
  locale?: string,
  pagination: boolean = true,
  query?: string,
  filteredFacets?: (FilteredFacet | FacetConfigurationsMappingType)[],
): Promise<Array<AlgoliaResponse>> => {
  console.log("🔍 [algoliaRefine] Called with:", {
    searchParams,
    filteredFacetsCount: filteredFacets?.length,
    categoryFilter,
  });

  // Use dynamic mapping if filteredFacets provided, else hardcoded
  const filterMappings = filteredFacets?.length ? buildDynamicFilterMappings(filteredFacets) : {};

  // Retrieve selected filters based on search parameters using the determined mappings
  const selectedFilters = getSelectedFiltersFromMap(searchParams, filterMappings);

  // Generate facet filters based on selected filters and search parameters
  const facetFilters = constructFacetFilters(selectedFilters, searchParams);

  //Get current refinements based on selected filters and search parameter
  const currentRefinements = constructCurrentRefinements(selectedFilters, searchParams);
  const currency = getCurrencyByLocale(locale ?? "");
  // Get price filters based on search parameters and current refinements
  let priceFilters = constructPriceFilters(searchParams, currentRefinements, currency);

  // Construct multi-query object for Algolia search
  const searchQuery = query || searchParams?.q || undefined;
  const multiQuery = constructQuery(
    indexName,
    facetFilters,
    searchParams,
    priceFilters,
    categoryFilter,
    pagination,
    "en",
    searchQuery,
    filterMappings,
  );
  try {
    console.log("******* ALGOLIA CONSTRUCT FILTER *******");

    // Process search results
    // MLC-878 - Commenting below line of clearing the cache
    // This kind of cache-clearing on the client-side is not
    // done or appears to be not necessary in Magrabi.
    // Hence commenting out this line for Doctor-M.
    //searchClient.clearCache();
    const response = await searchClient.search({
      requests: multiQuery.map((obj) => ({
        ...Object.values(obj)[0],
        clickAnalytics: true,
      })),
    });

    const searchResult = transformAlgoliaResponse(
      response?.results as Array<SearchResponse<ProductItem>>,
      language,
    );

    console.log("🔍 [algoliaRefine] Search results:", {
      mainNbHits: searchResult[0]?.nbHits,
      mainHitsLength: searchResult[0]?.hits?.length,
      allFacetsNbHits: searchResult[1]?.nbHits,
      facetFiltersApplied: facetFilters.length > 0,
      priceFiltersApplied: priceFilters.length > 0,
    });

    if (searchResult) {
      const results: Array<AlgoliaResponse> = multiQuery.map((obj, index) => {
        const key = Object.keys(obj)[0];
        const value = searchResult[index];
        const FacetObjectIn: FacetObject = (value?.facets as FacetObject) ?? {};
        const initialFacetsIn: FacetsData = {};
        const rangeFacetData: RangeFacetData = (value?.facets_stats as RangeFacetData) ?? {};

        // Extract price data
        const priceData = FacetObjectIn?.[facetAttributeNames.price];
        const priceFacet = facetAttributeNames?.price;
        Object.entries(FacetObjectIn).forEach(([attribute, attributeValues]) => {
          if (FacetObjectIn.hasOwnProperty(attribute)) {
            const attributeFacets = Object.entries(attributeValues).map(([value, count]) => ({
              count,
              isRefined:
                searchParams?.[getAttributeQueryParamKey(attribute, "en", filterMappings) ?? ""]
                  ?.split("|")
                  .includes(value) ?? false,
              value,
              label: value,
            }));

            // Include attribute facets except for price if not main query or if key is price facet
            if (attribute !== priceFacet || key === priceFacet) {
              initialFacetsIn[attribute] = attributeFacets;
            }
          }
        });

        // Prepare response object based on the key
        if (key === algoliaResults.MAIN) {
          return {
            [key]: {
              index: value?.index,
              queryID: value?.queryID,
              hits: value?.hits,
              facets: initialFacetsIn,
              nbHits: value?.nbHits,
              page: parseInt(searchParams?.page ?? "1", 10),
              nbPages: Math.ceil((value?.nbHits ?? 0) / hitsPerPage),
              query: value?.query,
              rangeFacetData,
              currentRefinements,
              priceData,
            },
          };
        }
        // Return other facet responses
        return { [key]: { hits: value?.hits, facets: initialFacetsIn, rangeFacetData, priceData } };
      });
      return results;
    }
  } catch (error) {
    console.error(error);
  }
  return [];
};

/**
 * @description Finds the Algolia search result with the specified key in the given array of Algolia responses.
 * @param {Array<AlgoliaResponse>} response - The array of Algolia responses.
 * @param {string} key - The key to search for within the Algolia responses.
 * @returns {AlgoliaSearchResult} The Algolia search result associated with the specified key, or an empty object if not found.
 */
export const findAlgoliaResponse = (
  response: Array<AlgoliaResponse>,
  key: string,
): AlgoliaSearchResult => {
  // Find the response object with the specified key and extract hits
  const hits =
    (response?.find((obj) => obj.hasOwnProperty(key)) as { [key: string]: AlgoliaSearchResult })?.[
      key
    ] ?? {};
  return hits;
};

/**
 * @description Constructs the name of the Algolia index based on the specified locale.
 * @param {string} locale - The locale for which the index name is constructed.
 * @returns {string} The name of the Algolia index corresponding to the specified locale.
 * @throws {Error} Throws an error if currency mappings are not found.
 */
export const constructAlgoliaIndexName = (locale: string): string => {
  // Parse currency mappings from environment variable
  // const currencyMappings = JSON.parse(process.env.CURRENCY_MAPPING ?? "");
  const curncyMappings = currencyMappings;

  // Throw an error if currency mappings are not found
  if (!curncyMappings) {
    throw new Error("Currency mappings not found!");
  }

  // Extract country code from locale
  const country = getCountryCodeFromLocale(locale).toLowerCase();
  const lang = getLangFromLocale(locale);
  const currencyKey = `${country}-${lang}`;
  // console.log({ currencyKey, locale });

  // Construct index name using locale, country code, and currency

  return `${indexNamePrefix}-${country}-web.${currencyMappings?.[locale]}.products`;
};

/**
 * @description Retrieves the name of the Algolia index corresponding to the specified sort option and locale.
 * @param {string} sort - The sort option for which the Algolia index name is retrieved.
 * @param {string} locale - The locale for which the Algolia index name is retrieved.
 * @param {Array<SortItem>} sortOptions - The list of sort items configured in cms.
 * @returns {string} The name of the Algolia index corresponding to the specified sort option and locale.
 */

export const getAlgoliaIndexName = (
  sort: string,
  locale: string,
  sortOptions: SortItem[] = [],
): string => {
  if (!hasArrayElements(sortOptions)) {
    return constructAlgoliaIndexName(locale);
  }
  const sortItem = sortOptions.find((option) => option.value === sort);
  const language = getLangFromLocale(locale);
  const { index, value } = sortItem || {};
  return (index && value && constructIndexName(value, index, language)) || sortOptions?.[0]?.index;
};

export const algoliaInit = () => {
  algoliaInsights(algoliaEventParam.INIT, {
    appId: applicationID,
    apiKey: searchApiKey,
  });
};

export const removeSameIdObjects = (
  recommendResult: SearchResponse<RecommendProductItem>[],
  productIds: string[],
): SearchResponse<RecommendProductItem>[] => {
  const idsSet: Set<string> = new Set(productIds);
  recommendResult?.forEach((result) => {
    if (hasArrayElements(result?.hits)) {
      result.hits = result?.hits?.filter(
        (item: RecommendProductItem) => !isAnchorProduct(item, idsSet),
      );
    }
  });
  return recommendResult;
};

export const removeDuplicates = (hits: RecommendProductItem[], productIds: string[]) => {
  const unique = hits?.filter((obj: RecommendProductItem, index: number) => {
    return (
      index === hits?.findIndex((item) => obj?.objectID === item?.objectID) &&
      !productIds?.includes(obj?.objectID as string)
    );
  });
  return unique;
};

export const algoliaRecommendation = async (
  productIds: string[],
  models: RecommendModels[],
  indexName: string,
  maxRecommendations: number,
  language: string,
  trendingCategory?: string,
): Promise<Record<string, RecommendProductItem[]>> => {
  const hasAnchoredModel = models?.some(
    (model) => model !== algoliaRecommendationParam?.TENDING_ITEMS,
  );
  if (
    !hasArrayElements(models) ||
    !indexName ||
    maxRecommendations <= 0 ||
    (hasAnchoredModel && !hasArrayElements(productIds))
  ) {
    return {};
  }
  const { requests, meta } = buildRecommendRequests(
    models,
    productIds ?? [],
    indexName,
    maxRecommendations,
    trendingCategory,
  );
  if (!hasArrayElements(requests)) return {};
  const cacheMetadata = { productIds, models, maxRecommendations, language, trendingCategory };
  try {
    const response = await algoliaRecommendationsCached(requests, cacheMetadata);
    let rawResults = response?.results as Array<SearchResponse<ProductItem>> | undefined;
    if (!rawResults) {
      // Algolia rejects the whole batch when one model is not available for the index, so retry each model on its own.
      console.error(
        `Algolia recommendation batch failed (index: ${indexName}, models: ${models.join(",")}), retrying per model`,
      );
      rawResults = new Array(requests.length);
      await Promise.all(
        Array.from(new Set(meta.map(({ model }) => model))).map(async (model) => {
          const positions = meta.flatMap((item, position) => (item.model === model ? [position] : []));
          const modelResponse = await algoliaRecommendationsCached(
            positions.map((position) => requests[position]),
            { ...cacheMetadata, models: [model] },
          );
          if (!modelResponse?.results) {
            console.error(
              `Algolia recommendation model unavailable (index: ${indexName}, model: ${model})`,
            );
          }
          positions.forEach((position, order) => {
            rawResults![position] = modelResponse?.results?.[order] as SearchResponse<ProductItem>;
          });
        }),
      );
    }
    const results = transformAlgoliaResponse(rawResults, language) as Array<
      SearchResponse<RecommendProductItem>
    >;
    return groupRecommendationsByModel(meta, results, productIds ?? [], indexName);
  } catch (err) {
    console.error(
      `Failed to fetch Algolia recommendations (index: ${indexName}, models: ${models.join(",")})`,
      getErrorMessage(err),
    );
    return {};
  }
};

const constructAlgoliaFilterByProductSlugs = (slugs: string[], type: string): string => {
  if (type === recommendationType?.SALEOR_COLLECTION) {
    return Array.isArray(slugs) ? slugs.map((slug) => `collections:${slug}`).join(" OR ") : "";
  } else {
    return Array.isArray(slugs)
      ? slugs.map((slug) => `defaultVariant.sku:${slug}`).join(" OR ")
      : "";
  }
};

export const getProductDetailsFromAlgoliaBySlugs = async (
  slugs: string[],
  indexName: string,
  type: string,
  productId: string[],
  maximumNoRecommendations: number,
  requestedLanguage?: string,
) => {
  const defaultLanguage = "en";
  const language = requestedLanguage ?? defaultLanguage;
  const searchRequest: MultipleQueries[] = [];
  if (Array.isArray(slugs) && type) {
    const filters = constructAlgoliaFilterByProductSlugs(slugs, type);
    searchRequest.push({
      indexName,
      hitsPerPage: maximumNoRecommendations,
      filters,
      clickAnalytics: true,
    });
  }
  const cacheMetadata = { slugs, type, productId, maximumNoRecommendations, language };

  let response: SearchResponse<ProductItem>[] = [];
  try {
    console.log("******* ALGOLIA FETCH BY SLUGS - PRODUCT CAROUSEL *******");
    const algoliaResponse = await algoliaSearchCached(searchRequest, cacheMetadata);
    const transformedResponse = transformAlgoliaResponse(
      algoliaResponse?.results as Array<SearchResponse<ProductItem>>,
      language,
    );
    response = removeSameIdObjects(
      transformedResponse as SearchResponse<RecommendProductItem>[],
      productId,
    );
  } catch (error) {
    console.error(error);
  }
  return response;
};

export const getCustomProductRecommendationBySku = (
  products: RecommendProductItem[],
  customProducts: ProductType[],
) => {
  let customProductRecommendation: RecommendProductItem[] = [];
  if (hasArrayElements(products) && hasArrayElements(customProducts)) {
    customProducts.forEach((customProduct) => {
      if (!customProduct?.slug || !customProduct?.sku) return;
      const product = products.find((product) => product?.slug === customProduct.slug);
      if (product && Array.isArray(product?.variantColorMappings)) {
        const matchingVariant = product.variantColorMappings.find(
          (variant) => variant?.sku === customProduct.sku,
        );
        if (matchingVariant) {
          customProductRecommendation.push({
            ...product,
            defaultVariant: matchingVariant,
          });
        }
      }
    });
  }
  return customProductRecommendation;
};

const getManualRecommendationItems = async (
  entry: RecommendationType,
  productId: string[],
  indexName: string,
  language: string,
  maxProducts: number,
): Promise<RecommendProductItem[]> => {
  const values = getManualSourceValues(entry);
  if (!values || !hasArrayElements(values)) return [];
  const response = await getProductDetailsFromAlgoliaBySlugs(
    values,
    indexName,
    entry.type,
    productId,
    maxProducts,
    language,
  );
  const result = response?.[0];
  if (!hasArrayElements(result?.hits)) return [];
  const products = result.hits.map((product: AlgoliaProductItem) => ({
    ...product,
    queryID: result?.queryID ?? "",
    indexName,
  }));
  return isSkuSource(entry) ? getCustomProductRecommendationBySku(products, entry.products || []) : products;
};

// All sources are fetched at the same time (one Algolia round trip instead of one per source), then put
// together in the order set in Sanity. Model sources pass their Algolia model name (`model`), not the source type.
export const getRecommendationProducts = async (
  productId: string[],
  recommendation: Recommendation,
  locale: string,
): Promise<RecommendProductItem[]> => {
  const sources = recommendation?.types || [];
  if (!hasArrayElements(sources)) return [];
  const indexName = getAlgoliaIndexName("", locale);
  const language = getLangFromLocale(locale);
  const maxProducts: number = recommendationType?.MAX_RECOMMENDATIONS;
  const isManualSource = (source: RecommendationType) => getManualSourceValues(source) !== null;
  const trendingCategory = sources.find(
    (source) => source?.type === recommendationType?.TRENDING,
  )?.category;
  const [recommendedByModel, manualItems] = await Promise.all([
    algoliaRecommendation(
      productId,
      getRecommendationModels(recommendation),
      indexName,
      maxProducts,
      language,
      trendingCategory,
    ),
    Promise.all(
      sources.map((source) =>
        isManualSource(source)
          ? getManualRecommendationItems(source, productId, indexName, language, maxProducts)
          : undefined,
      ),
    ),
  ]);
  const entries = sources.map((source, position) =>
    isManualSource(source)
      ? { manualItems: manualItems[position] || [] }
      : { model: source?.model },
  );
  return assembleCarousel(entries, recommendedByModel, maxProducts);
};

export const convertProductListToMap = (
  productList: RecommendProductItem[],
): Record<string, RecommendProductItem> => {
  const productDataMap: Record<string, RecommendProductItem> = {};
  if (hasArrayElements(productList)) {
    productList.forEach((each) => {
      if (each?.slug) {
        productDataMap[each?.slug] = each;
      }
    });
  }
  return productDataMap;
};

/**
 * Builds a variant-aware product map keyed by `${slug}-${sku}`.
 *
 * Unlike `convertProductListToMap` (which collapses all variants of a product
 * under a single slug key), this preserves each individual Algolia hit so that
 * manually configured carousels can render multiple variants of the same
 * parent product as configured in Sanity.
 */
export const convertProductListToVariantMap = (
  productList: RecommendProductItem[],
): Record<string, RecommendProductItem> => {
  const productVariantMap: Record<string, RecommendProductItem> = {};
  if (hasArrayElements(productList)) {
    productList.forEach((each) => {
      const key = each?.defaultVariant?.sku
        ? `${each?.slug}-${each?.defaultVariant?.sku}`
        : each?.slug;
      if (key) {
        // Base record goes under `base:<key>` and never replaces a variant record.
        const isBase = each?.groupedIndex === false && !each?.parentProductId;
        if (isBase) productVariantMap[`base:${key}`] = each;
        if (!isBase || !productVariantMap[key]) productVariantMap[key] = each;
      }
    });
  }
  return productVariantMap;
};

export const getMatchingTopsForPairEyewear = async (
    locale: string,
    hitsPerPage?: number,
    model?: string,
    brand?: string,
  ): Promise<RecommendProductItem[]> => {
    let productList: RecommendProductItem[] = [];
    const indexName = getAlgoliaIndexName("", locale);
    const language = getLangFromLocale(locale);
    const searchRequest: MultipleQueries[] = [];
    searchRequest.push({
      indexName,
      ...(hitsPerPage && { hitsPerPage }),
      filters: `brand.name.en:"${brand}" AND categories.en.lvl0:"Accessories" AND attributes.en.clipon_style.code:"${model}"`,
      clickAnalytics: true,
    });

    try {
        console.log("******* ALGOLIA FETCH FOR PAIR EYEWEAR *******");
      const algoliaResponse = await searchClient.search({ requests: searchRequest });
      const translatedResults = transformAlgoliaResponse(
        algoliaResponse.results as Array<SearchResponse<ProductItem>>,
        language,
      );
      if (hasArrayElements(translatedResults)) {
        translatedResults?.forEach((result) => {
          const resultData = result as SearchResponse<RecommendProductItem>;
          if (hasArrayElements(resultData?.hits)) {
            productList = [...productList, ...resultData.hits];
          }
        });
      }
    } catch (error) {
      console.error(error);
    }
    return productList;
};

export const getProductsBySlugsForHomePage = async (
  slugs: string[],
  locale: string,
  hitsPerPage?: number,
): Promise<RecommendProductItem[]> => {
  let productList: RecommendProductItem[] = [];
  if (hasArrayElements(slugs)) {
    const indexName = getAlgoliaIndexName("", locale);
    const language = getLangFromLocale(locale);
    const searchRequest: MultipleQueries[] = [];
    searchRequest.push({
      indexName,
      ...(hitsPerPage && { hitsPerPage }),
      filters: slugs.join(" OR "),
      clickAnalytics: true,
    });
    const cacheMetadata = { slugs, hitsPerPage, language };
    try {
      console.log("******* ALGOLIA FETCH BY SLUGS - HOME-PAGE *******");
      const algoliaResponse = await algoliaSearchCached(searchRequest, cacheMetadata);
      const translatedResults = transformAlgoliaResponse(
        algoliaResponse.results as Array<SearchResponse<ProductItem>>,
        language,
      );
      if (hasArrayElements(translatedResults)) {
        translatedResults?.forEach((result) => {
          const resultData = result as SearchResponse<RecommendProductItem>;
          if (hasArrayElements(resultData?.hits)) {
            productList = [...productList, ...resultData.hits];
          }
        });
      }
    } catch (error) {
      console.error(error);
    }
  }
  return productList;
};

export const getProductRecommendationHomePage = async (
  models: RecommendModels[],
  locale: string,
): Promise<RecommendProductItem[]> => {
  const trendingProducts: RecommendProductItem[] = [];
  if (hasArrayElements(models)) {
    const indexName = getAlgoliaIndexName("", locale);
    const reqObj: RecommendedRequest[] = models.map((model) => ({
      indexName,
      model,
      maxRecommendations: recommendationType?.MAX_RECOMMENDATIONS,
      threshold: 8,
    }));
    const language = getLangFromLocale(locale);
    const cacheMetadata = { models, language };
    try {
      console.log("******* ALGOLIA RECOMMENDATION FETCH - HOME-PAGE *******");
      const algoliaResponse = await algoliaRecommendationsCached(
        reqObj as RecommendationsRequest[],
        cacheMetadata
      );
      const results = algoliaResponse?.results as Array<SearchResponse<ProductItem>>;
      const transformedResults = transformAlgoliaResponse(results, language);
      if (hasArrayElements(transformedResults)) {
        transformedResults?.forEach((result) => {
          const resultData = result as SearchResponse<RecommendProductItem>;
          if (hasArrayElements(resultData?.hits)) {
            resultData.hits.forEach((hit) => {
              trendingProducts.push(hit);
            });
          }
        });
      }
    } catch (error) {
      console.error(error);
    }
  }
  return trendingProducts;
};

/**
 * @description Constructs a multi-query for Algolia search in sale page.
 * @param {string} indexName - The name of the index.
 * @param {Set<string>} categorySlugs - A set of category slugs to include in the multi-query.
 * @param {Set<string>} collectionSlugs -  A set of collection slugs to include in the multi-query.
 * @param {Set<string>} productSlugs -  A set of product slugs to include in the multi-query.
 * @returns {Promise<Array<SalePageMultiQuery>>} - A promise resolving to an array containing multi-query items.
 */
const constructMultiQueryForSalePage = async (
  indexName: string,
  categorySlugs: Set<string>,
  collectionSlugs: Set<string>,
  productSlugs: Set<string>,
  language: string,
): Promise<Array<SalePageMultiQuery>> => {
  const multiQuery: Array<SalePageMultiQuery> = [];

  if (productSlugs?.size > 0) {
    multiQuery.push({
      bannerGridProducts: {
        indexName,
        filters: Array.from(productSlugs)?.join(" OR "),
      },
    });
  }

  if (categorySlugs?.size > 0) {
    for (const slug of Array.from(categorySlugs)) {
      const languageCode: LanguageCodeEnum =
        language === "en" ? LanguageCodeEnum.En : LanguageCodeEnum.Ar;
      const category = await getCategoryBySlug(slug, languageCode);
      const categoryName = category
        ? getValueBasedOnLanguage(language, category, "name", "name", category?.translation)
        : "";
      if (categoryName) {
        multiQuery.push({
          [slug]: {
            indexName,
            hitsPerPage: salePageConstants.HITS_COUNT,
            // With max of 2 level category structure "universal" would be the root category
            filters:
              category?.parent?.slug === "universal"
                ? `categories.${language}.lvl0: "${categoryName}"`
                : `categories.${language}.lvl1: "${categoryName}"`,
          },
        });
      }
    }
  }

  if (collectionSlugs?.size > 0) {
    collectionSlugs.forEach((slug) => {
      multiQuery.push({
        [slug]: {
          indexName,
          hitsPerPage: salePageConstants.HITS_COUNT,
          filters: `collections: "${slug}"`,
        },
      });
    });
  }
  return multiQuery;
};

export const getProductsBySlugsForSalePage = async (
  categorySlugs: Set<string>,
  collectionSlugs: Set<string>,
  productSlugs: Set<string>,
  locale: string,
): Promise<Array<Record<string, SalePageAlgoliaResponse>>> => {
  const indexName = constructAlgoliaIndexName(locale);
  const language = getLangFromLocale(locale);
  const multiQuery = await constructMultiQueryForSalePage(
    indexName,
    categorySlugs,
    collectionSlugs,
    productSlugs,
    language,
  );
  const cacheMetadata = {
    categorySlugs: Array.from(categorySlugs).sort(),
    collectionSlugs: Array.from(collectionSlugs).sort(),
    productSlugs: Array.from(productSlugs).sort(),
    language,
  };
  try {
    console.log("******* ALGOLIA FETCH BY SLUGS - SALE-PAGE *******");
    const response = await algoliaSearchCached(
      multiQuery.map((obj) => ({
        ...Object.values(obj)[0],
        clickAnalytics: true,
      })),
      cacheMetadata,
    );
    const searchResult = transformAlgoliaResponse(
      response?.results as Array<SearchResponse<ProductItem>>,
      language,
    );

    return multiQuery.map((obj, index) => ({
      [Object.keys(obj)[0]]: {
        hits: searchResult[index]?.hits || [],
        nbHits: searchResult[index]?.nbHits || 0,
      },
    }));
  } catch (error) {
    console.error("Error fetching products:", error);
  }
  return [];
};

const constructFilterByAttribute = async (
  type: EditsType,
  attribute: EditsItemAttribute,
  language: string,
): Promise<string> => {
  if (type === editsSectionType.CATEGORY) {
    const languageCode: LanguageCodeEnum =
      language === "en" ? LanguageCodeEnum.En : LanguageCodeEnum.Ar;
    const category = await getCategoryBySlug(attribute?.slug, languageCode);
    const categoryName = category
      ? getValueBasedOnLanguage(language, category, "name", "name", category?.translation)
      : "";
    if (category) {
      return category?.parent?.slug === "universal"
        ? `categories.${language}.lvl0: "${categoryName}"`
        : `categories.${language}.lvl1: "${categoryName}"`;
    }
  }
  if (type === editsSectionType.COLLECTION) {
    return `collections:"${attribute?.slug}"`;
  }
  return "";
};

export const getProductsByAttribute = async (
  locale: string,
  attributeType: EditsType,
  attribute: EditsItemAttribute,
): Promise<Array<SalePageAlgoliaResponse>> => {
  const language = getLangFromLocale(locale);
  const filter = await constructFilterByAttribute(attributeType, attribute, language);
  if (!filter) return [];
  const indexName = constructAlgoliaIndexName(locale);
  const searchRequest = [
    {
      indexName,
      hitsPerPage: salePageConstants.HITS_COUNT,
      filters: filter,
      clickAnalytics: true,
    },
  ];
  const cacheMetadata = { attributeType, attribute, language };
  try {
    console.log("******* ALGOLIA GET PRODUCT BY ATTRIBUTE *******");
    const algoliaResponse = await algoliaSearchCached(searchRequest, cacheMetadata);
    const results = transformAlgoliaResponse(
      algoliaResponse?.results as Array<SearchResponse<ProductItem>>,
      language,
    );
    const result = results?.[0] as SalePageAlgoliaResponse;
    return result ? [{ nbHits: result?.nbHits, hits: result?.hits }] : [];
  } catch (error) {
    console.error("Error fetching products:", error);
    return [];
  }
};

/**
 * @description Constructs a multi-query object for Algolia search.
 * @param {string} indexName - The name of the index.
 * @param {string} searchQuery - The Algolia search query.
 * @param {string} language - language en/ar
 * @returns {Array<MultiQueryItem>} An array containing multi-query items.
 */
const constructSearchSuggestionQuery = (
  indexName: string,
  searchQuery?: string,
  language: string = "en",
) => {
  const multiQuery: Array<MultiQueryItem> = [
    {
      querySearch: {
        indexName,
        query: searchQuery,
        hitsPerPage: 5,
      },
    },
    {
      querySuggestion: {
        indexName: `${indexName}_query_suggestions_${language}`,
        query: searchQuery,
        hitsPerPage: 5,
      },
    },
  ];

  return multiQuery;
};

/**
 * @description Performs refinement operations on Algolia search results.
 * @param {string} indexName - The name of the index.
 * @param {string}searchQuery
 * @param {boolean} [pagination=false] - Flag indicating whether pagination is enabled.
 * @returns {Promise<Array<AlgoliaResponse>>} A promise resolving to an array containing refined Algolia search responses.
 */
export const algoliaSearchRefine = async (
  indexName: string,
  language: string,
  searchQuery?: string,
): Promise<AlgoliaSearchSuggestionResult> => {
  // Construct multi-query object for Algolia search
  const multiQuery = constructSearchSuggestionQuery(indexName, searchQuery, language);
  try {
    // Process search results
    console.log("******* ALGOLIA SEARCH SUGGESTIONS *******");
    const response = await searchClient.search({
      requests: multiQuery.map((obj) => ({
        ...Object.values(obj)[0],
        clickAnalytics: true,
      })),
    });
    const searchResult = transformAlgoliaResponse(
      response?.results as Array<SearchResponse<ProductItem>>,
      language,
    );
    return {
      products: searchResult[0] as SearchResponse<ProductItem>,
      suggestions: response?.results?.[1] as SearchForFacetValuesResponse,
    };
  } catch (error) {
    console.error(error);
  }
  return {
    products: {} as SearchResponse<ProductItem>,
    suggestions: {} as SearchForFacetValuesResponse,
  };
};

const getTranslatedFacetsAttributes = (language: string = "en") => [
  `brand.name.${language}`,
  `attributes.${language}.Feature`,
  `attributes.${language}.shape.attributes.label`,
  `attributes.${language}.frameTypeEnum.attributes.label`,
  `attributes.${language}.clipon_style.attributes.label`,
  `defaultVariant.attributes.${language}.fStyleEnum.attributes.label`,
  `defaultVariant.attributes.${language}.fcCodeEnum.attributes.color_family`,
  `defaultVariant.attributes.${language}.lenseColor.attributes.label`,
  `defaultVariant.attributes.${language}.lenseColor.attributes.color_family`,
  `defaultVariant.attributes.${language}.clColorEnum.attributes.color_family`,
  `attributes.${language}.frameMaterialEnum.attributes.label`,
  `attributes.${language}.acPrdMatEnumCode.attributes.label`,
  `categories.${language}.lvl0`,
  `defaultVariant.attributes.${language}.clPerPackSize`,
  `defaultVariant.attributes.${language}.clUsage`,
  `defaultVariant.attributes.${language}.clCorrVisEnum`,
  `attributes.${language}.acPrdCatgEnumCode`,
  `defaultVariant.attributes.${language}.genderEnum`,
  "defaultVariant.vtoEnabled",
  "defaultVariant.uvProtection",
  "defaultVariant.pricing.price.gross",
];

export const getHierarchicalMenuItems = (
  language: string = "en",
  translations?: TranslationData,
) => [
  {
    title: translations?.common?.common_header_search_categories_title,
    attribute: `categories.${language}.lvl0`,
  },
  {
    title: translations?.common?.common_header_search_brands_title,
    attribute: `brand.name.${language}`,
  },
];

export const facetAttributeNames = {
  features: `attributes.en.Feature`,
  brands: `brand.name.en`,
  price: "defaultVariant.pricing.price.gross",
  shape: `attributes.en.shape.attributes.label`,
  frameType: `attributes.en.frameTypeEnum.attributes.label`,
  fit: `defaultVariant.attributes.en.fStyleEnum.attributes.label`,
  clipon_style: `attributes.en.clipon_style.attributes.label`,
  frameColor: `defaultVariant.attributes.en.fcCodeEnum.attributes.color_family`,
  lensColor: `defaultVariant.attributes.en.lenseColor.attributes.color_family`,
  clColor: `defaultVariant.attributes.en.clColorEnum.attributes.color_family`,
  material: `attributes.en.frameMaterialEnum.attributes.label`,
  acPrdMatEnumCode: `attributes.en.acPrdMatEnumCode.attributes.label`,
  categoryFilter: `categories.en.lvl0`,
  packFilter: `defaultVariant.attributes.en.clPerPackSize`,
  disposabilityFilter: `defaultVariant.attributes.en.clUsage`,
  focalTypeFilter: `defaultVariant.attributes.en.clCorrVisEnum`,
  accesoriesTypeFilter: `attributes.en.acPrdCatgEnumCode`,
  genderFilter: `defaultVariant.attributes.en.genderEnum`,
  vtoFilter: "defaultVariant.vtoEnabled",
  uvProtection: "defaultVariant.uvProtection",
  polarized: `defaultVariant.attributes.en.polarized`,
  accGender: `defaultVariant.attributes.en.acGenderEnumCode`,
  accColor: `defaultVariant.attributes.en.accColorFamilyEnumCode.attributes.color_family`,
  accVolume: `defaultVariant.attributes.en.volume`,
  uvBlock: `defaultVariant.attributes.en.uvBlock`,
  clMaterial: `defaultVariant.attributes.en.clMatEnum.attributes.label`,
  clDuration: `attributes.en.windEnum`,
};

export const buildDynamicFilterMappings = (
  filters: (FilteredFacet | FacetConfigurationsMappingType)[],
): Record<string, string> => {
  const dynamic: Record<string, string> = {};

  filters.forEach((filter) => {
    const f = filter as FilteredFacet & FacetConfigurationsMappingType;
    const urlKey = f.filterParams;
    const facetAttributeEn = f.facetAttributeEn;

    if (urlKey && facetAttributeEn) {
      dynamic[urlKey] = facetAttributeEn;
    }

    if (Array.isArray(f.booleanMappings)) {
      f.booleanMappings.forEach((mapping) => {
        if (mapping.filterParams && mapping.facetField) {
          dynamic[mapping.filterParams] = mapping.facetField;
        }
      });
    }
  });

  return dynamic;
};

/**
 * @description Builds a dynamic searchParamsInitial object from filteredFacets.
 * Builds from filters[].filterParams + fixed keys (pmin, pmax, page, sort, q).
 */
export const buildDynamicSearchParamsInitial = (filters: FilteredFacet[]): Record<string, null> => {
  const dynamic: Record<string, null> = {
    pmin: null,
    pmax: null,
    page: null,
    sort: null,
    q: null,
  };

  filters.forEach((filter) => {
    if (filter.filterParams) {
      dynamic[filter.filterParams] = null;
    }
  });

  return dynamic;
};
export const getAlgoliaSearchQueryParams = (
  language: string = "en",
): { [key: string]: string } => ({
  [`brand.name.${language}`]: "b",
  [`categories.${language}.lvl0`]: "cat",
  q: "q",
});
